/**
 * Reproductor del registro de eventos (ADR 0007): responde «¿qué estado había en el segundo t?».
 * La escena 3D solo dibuja ese estado; los traslados se interpolan sobre la ruta registrada.
 * Pausa, «siguiente evento», ×60–×600, ir al cierre y repetición son operaciones del reproductor,
 * no del motor: la velocidad visual nunca altera los resultados.
 */

import type { ConfiguracionDeEjecucion, Evento } from '../dominio/ejecucion';
import type { Punto } from '../dominio/espacio';
import type { Rol } from '../dominio/proceso';
import { SEGUNDOS_POR_DIA } from '../dominio/tiempo';
import type { ModeloEspacial } from '../espacial/compilador';
import { longitud, puntoEnPolilinea } from '../espacial/geometria';

export type EstadoVisualUnidad =
  'cola-ingreso' | 'traslado' | 'actividad' | 'espera' | 'espera-externa';

export interface UnidadVisual {
  id: string;
  placa: string;
  tipoUnidad: string;
  x: number;
  z: number;
  /** Rumbo en radianes (convención de rotación del dominio). */
  rumbo: number;
  estado: EstadoVisualUnidad;
  etapaId: string | null;
  estacionId: string | null;
  listo: boolean;
  atrasada: boolean;
  horaPrometida: number | null;
  /**
   * Una persona de la etapa está en un recorrido a pie (paso 1.6): la escena no la dibuja en su
   * puesto fijo junto a la estación, porque camina. Si camina el cliente, no se marca.
   */
  aPie?: true;
}

/** Persona en un recorrido a pie en un instante (paso 1.6, avance 2). */
export interface PersonaVisual {
  /** Recurso (T01, J01…) o «cliente-» más la OT. */
  id: string;
  rol: Rol | 'cliente';
  x: number;
  z: number;
  rumbo: number;
  /** Camina (si no, lo atienden en el destino y está quieto). */
  camina: boolean;
}

export interface EstacionVisual {
  id: string;
  x: number;
  z: number;
  rotacion: number;
}

/** Punto de una ruta; con `s`, el metro del recorrido por el eje de las vías (el del motor). */
type PuntoDeRuta = Punto & { s?: number };

interface Tramo {
  desde: number;
  hasta: number;
  estado: EstadoVisualUnidad;
  etapaId: string | null;
  estacionId: string | null;
  ruta?: PuntoDeRuta[];
  largo?: number;
  aPie?: true;
}

interface TramoAPie {
  desde: number;
  hasta: number;
  id: string;
  rol: Rol | 'cliente';
  ruta: Punto[];
  largo: number;
  camina: boolean;
}

interface LineaUnidad {
  id: string;
  placa: string;
  tipoUnidad: string;
  llegada: number;
  salida: number;
  tramos: Tramo[];
  /**
   * Horas prometidas desde cada instante: la de la apertura de la OT y las que la corren (adicional
   * aprobado, paso 1.6). En cada instante vale la última vigente.
   */
  promesas: { desde: number; hora: number }[];
  listo: number | null;
}

export interface Reproductor {
  readonly inicio: number;
  readonly fin: number;
  /** Instantes con eventos, ordenados y sin repetir. */
  readonly instantes: readonly number[];
  estadoEn(t: number): UnidadVisual[];
  /** Personas en un recorrido a pie en el segundo t (paso 1.6). */
  personasEn(t: number): PersonaVisual[];
  /**
   * Autos de la cola del ingreso que no caben en la vía de acceso en el segundo t (paso 1.6,
   * avance 3). Sin capacidad conocida, 0.
   */
  enLaCalle(t: number): number;
  eventosEntre(a: number, b: number): Evento[];
  /** Próximo instante con eventos estrictamente posterior a t (o el fin). */
  siguienteEvento(t: number): number;
}

export interface OpcionesReproductor {
  eventos: readonly Evento[];
  estaciones: readonly EstacionVisual[];
  /**
   * Polilíneas por «origen>destino»; si falta, el traslado se dibuja en línea recta. Si sus puntos
   * traen `s`, el auto avanza por esos metros (los del motor) y no por el largo dibujado.
   */
  rutas: Readonly<Record<string, PuntoDeRuta[]>>;
  /**
   * Caminos a pie por «desde>hacia» (B01>ALM…; `caminosAPie`). Si falta, entre dos estaciones se
   * dibuja en línea recta; hacia un punto sin camino, la persona no se dibuja.
   */
  caminosAPie?: Readonly<Record<string, Punto[]>>;
  /**
   * Maniobra de cada traslado (s), con movimiento por distancia: la mitad quieto en el origen y la
   * mitad en el destino, como en el motor (ADR 0021, avance 3). Sin ella, velocidad pareja.
   */
  maniobraS?: number;
  /** Cola del ingreso del modelo espacial: hacia dónde crece y cuántos autos caben en el acceso. */
  colaIngreso?: { direccion: Punto; capacidad: number } | null;
  inicio: number;
  fin: number;
  /** Separación entre autos en la cola de la calle (m). */
  separacionCola?: number;
}

/**
 * Reproducción de varias jornadas (paso 1.6, avance 4): la noche se salta. Después del cierre de un
 * día se pasa al día siguiente a `inicioDia` (segundo del día; la reproducción empieza diez minutos
 * antes del turno), y antes de esa hora en los días 2 en adelante, también. Solo cambia lo que se ve.
 */
export function sinNoche(t: number, inicioDia: number, cierre: number): number {
  const dia = Math.floor(t / SEGUNDOS_POR_DIA) + 1;
  const s = t - (dia - 1) * SEGUNDOS_POR_DIA;
  if (s > cierre) return dia * SEGUNDOS_POR_DIA + inicioDia;
  if (dia > 1 && s < inicioDia) return (dia - 1) * SEGUNDOS_POR_DIA + inicioDia;
  return t;
}

/**
 * Lo que el reproductor toma del modelo espacial y de la configuración: estaciones, rutas, cola del
 * ingreso y maniobra (con movimiento por distancia).
 */
export function escenaDelModelo(
  modelo: Pick<ModeloEspacial, 'estaciones' | 'rutas' | 'colaIngreso'>,
  config: Pick<ConfiguracionDeEjecucion, 'traslados'>,
): Pick<OpcionesReproductor, 'estaciones' | 'rutas' | 'colaIngreso' | 'maniobraS'> {
  return {
    estaciones: modelo.estaciones.map((e) => ({
      id: e.id,
      x: e.centro.x,
      z: e.centro.z,
      rotacion: e.rotacion,
    })),
    rutas: modelo.rutas,
    colaIngreso: modelo.colaIngreso,
    ...(config.traslados.modo === 'distancia' ? { maniobraS: config.traslados.maniobraS } : {}),
  };
}

function buscarTramo(tramos: readonly Tramo[], t: number): Tramo | null {
  let lo = 0;
  let hi = tramos.length - 1;
  let r: Tramo | null = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const tr = tramos[mid]!;
    if (tr.desde <= t) {
      r = tr;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return r;
}

export function crearReproductor(o: OpcionesReproductor): Reproductor {
  const estaciones = new Map(o.estaciones.map((e) => [e.id, e]));
  const lineas = new Map<string, LineaUnidad>();
  const aPie: TramoAPie[] = [];
  const instantes = new Set<number>();
  const separacion = o.separacionCola ?? 6;

  const linea = (id: string) => lineas.get(id);
  const cerrar = (l: LineaUnidad, t: number) => {
    const ultimo = l.tramos[l.tramos.length - 1];
    if (ultimo && ultimo.hasta === Infinity) ultimo.hasta = t;
  };
  const abrir = (l: LineaUnidad, t: number, tr: Omit<Tramo, 'desde' | 'hasta'>) => {
    cerrar(l, t);
    l.tramos.push({ ...tr, desde: t, hasta: Infinity });
  };
  /** Sigue en el mismo estado, con o sin alguien a pie. */
  const marcarAPie = (l: LineaUnidad, t: number, enRecorrido: boolean) => {
    const u = l.tramos[l.tramos.length - 1];
    if (!u) return;
    abrir(l, t, {
      estado: u.estado,
      etapaId: u.etapaId,
      estacionId: u.estacionId,
      ...(enRecorrido ? { aPie: true as const } : {}),
    });
  };
  const caminoEntre = (desde: string, hacia: string): Punto[] => {
    const c = o.caminosAPie?.[`${desde}>${hacia}`];
    if (c) return c;
    const a = estaciones.get(desde);
    const b = estaciones.get(hacia);
    if (!a || !b) return [];
    return desde === hacia
      ? [{ x: a.x, z: a.z }]
      : [
          { x: a.x, z: a.z },
          { x: b.x, z: b.z },
        ];
  };

  for (const e of o.eventos) {
    if (e.unidadId) instantes.add(e.t);
    if (e.tipo === 'unidad.creada' && e.unidadId) {
      lineas.set(e.unidadId, {
        id: e.unidadId,
        placa: String(e.atributos?.placa ?? ''),
        tipoUnidad: String(e.atributos?.tipoUnidad ?? ''),
        llegada: Infinity,
        salida: Infinity,
        tramos: [],
        promesas: [],
        listo: null,
      });
      continue;
    }
    const l = e.unidadId ? linea(e.unidadId) : undefined;
    if (!l) continue;
    switch (e.tipo) {
      case 'unidad.llegada':
        l.llegada = e.t;
        abrir(l, e.t, {
          estado: 'cola-ingreso',
          etapaId: null,
          estacionId: e.ubicacion?.estacionId ?? null,
        });
        break;
      case 'traslado.inicio': {
        const tr = e.traslado!;
        const desde = estaciones.get(tr.origen);
        const hasta = estaciones.get(tr.destino);
        const ruta =
          o.rutas[tr.rutaId] ??
          (desde && hasta
            ? [
                { x: desde.x, z: desde.z },
                { x: hasta.x, z: hasta.z },
              ]
            : []);
        abrir(l, e.t, {
          estado: 'traslado',
          etapaId: e.etapaId ?? null,
          estacionId: tr.destino,
          ruta,
          largo:
            ruta.length > 0 && ruta.every((q) => q.s !== undefined)
              ? ruta[ruta.length - 1]!.s!
              : longitud(ruta),
        });
        // El traslado dura lo que dice el registro (aunque no llegue un evento de fin en el horizonte).
        l.tramos[l.tramos.length - 1]!.hasta = e.t + tr.duracionS;
        break;
      }
      case 'traslado.fin': {
        const ultimo = l.tramos[l.tramos.length - 1];
        if (ultimo) ultimo.hasta = e.t;
        l.tramos.push({
          desde: e.t,
          hasta: Infinity,
          estado: 'espera',
          etapaId: e.etapaId ?? null,
          estacionId: e.ubicacion?.estacionId ?? null,
        });
        break;
      }
      case 'etapa.inicio':
      case 'etapa.reanudacion':
        abrir(l, e.t, {
          estado: e.categoria === 'espera-cliente' ? 'espera-externa' : 'actividad',
          etapaId: e.etapaId ?? null,
          estacionId: e.ubicacion?.estacionId ?? null,
        });
        break;
      case 'etapa.interrupcion':
        abrir(l, e.t, {
          estado: 'espera-externa',
          etapaId: e.etapaId ?? null,
          estacionId: e.ubicacion?.estacionId ?? null,
        });
        break;
      case 'espera.inicio':
      case 'etapa.fin': {
        const enIngreso = e.ubicacion?.estacionId === 'ING';
        const ultimo = l.tramos[l.tramos.length - 1];
        if (ultimo?.estado === 'cola-ingreso') break;
        abrir(l, e.t, {
          estado: enIngreso ? 'cola-ingreso' : 'espera',
          etapaId: e.etapaId ?? null,
          estacionId: e.ubicacion?.estacionId ?? null,
        });
        break;
      }
      case 'recorrido.inicio': {
        const a = e.atributos ?? {};
        const duracion = Number(a.duracionS ?? 0);
        const ruta = caminoEntre(String(a.desde), String(a.hacia));
        const quien = String(a.quien);
        if (duracion > 0 && ruta.length > 0)
          aPie.push({
            desde: e.t,
            hasta: e.t + duracion,
            id: quien === 'cliente' ? `cliente-${l.id}` : (e.recursos?.[0]?.id ?? quien),
            rol: quien as Rol | 'cliente',
            ruta,
            largo: longitud(ruta),
            camina: a.tipo === 'camina',
          });
        marcarAPie(l, e.t, quien !== 'cliente');
        break;
      }
      case 'recorrido.fin':
        marcarAPie(l, e.t, false);
        break;
      case 'unidad.hito':
        if (
          (e.atributos?.hito === 'apertura-ot' || e.atributos?.hito === 'promesa-corrida') &&
          typeof e.atributos.horaPrometida === 'number'
        ) {
          l.promesas.push({ desde: e.t, hora: e.atributos.horaPrometida });
        }
        if (e.atributos?.hito === 'listo') l.listo = e.t;
        break;
      case 'unidad.salida':
        l.salida = e.t;
        cerrar(l, e.t);
        break;
      default:
        break;
    }
  }
  for (const l of lineas.values()) {
    for (const tr of l.tramos) if (tr.hasta === Infinity) tr.hasta = Math.max(o.fin, tr.desde);
  }
  const lista = [...lineas.values()].filter((l) => l.llegada !== Infinity);
  const ordenInstantes = [...instantes].sort((a, b) => a - b);
  const ordenEventos = [...o.eventos];
  const ingreso = estaciones.get('ING');

  // Dirección de la cola en la calle: la del modelo espacial o, si falta, opuesta a la primera ruta
  // que sale del ingreso.
  let colaDir = o.colaIngreso?.direccion ?? { x: 0, z: 1 };
  const primera = Object.entries(o.rutas).find(([k]) => k.startsWith('ING>'))?.[1];
  if (!o.colaIngreso && primera && primera.length >= 2) {
    const a = primera[0]!;
    const b = primera[1]!;
    const L = Math.hypot(b.x - a.x, b.z - a.z) || 1;
    colaDir = { x: -(b.x - a.x) / L, z: -(b.z - a.z) / L };
  }
  const capacidadCola = o.colaIngreso?.capacidad;
  const maniobra = o.maniobraS ?? 0;

  return {
    inicio: o.inicio,
    fin: o.fin,
    instantes: ordenInstantes,
    estadoEn(t: number): UnidadVisual[] {
      const r: UnidadVisual[] = [];
      const enCola: { l: LineaUnidad; tr: Tramo }[] = [];
      for (const l of lista) {
        if (t < l.llegada || t >= l.salida) continue;
        const tr = buscarTramo(l.tramos, t);
        if (!tr) continue;
        if (tr.estado === 'cola-ingreso') {
          enCola.push({ l, tr });
          continue;
        }
        let x = 0;
        let z = 0;
        let rumbo = 0;
        if (tr.estado === 'traslado' && tr.ruta && tr.ruta.length > 0) {
          const dur = Math.max(1, tr.hasta - tr.desde);
          // Maniobra: la mitad quieto al salir y la mitad al llegar; entre medio, velocidad pareja.
          const medio = Math.min(maniobra, dur) / 2;
          const marcha = dur - 2 * medio;
          const f =
            marcha > 0
              ? Math.min(1, Math.max(0, (t - tr.desde - medio) / marcha))
              : t - tr.desde >= dur / 2
                ? 1
                : 0;
          const p =
            tr.ruta[0]!.s !== undefined
              ? puntoPorMetro(tr.ruta, f * (tr.largo ?? 0))
              : puntoEnPolilinea(tr.ruta, f * (tr.largo ?? 0));
          x = p.punto.x;
          z = p.punto.z;
          rumbo = p.rumbo;
        } else {
          const est = tr.estacionId ? estaciones.get(tr.estacionId) : undefined;
          if (est) {
            x = est.x;
            z = est.z;
            rumbo = est.rotacion;
          }
        }
        r.push(visual(l, tr, x, z, rumbo, t));
      }
      enCola.sort((a, b) => a.l.llegada - b.l.llegada || (a.l.id < b.l.id ? -1 : 1));
      enCola.forEach(({ l, tr }, k) => {
        const base = ingreso ?? { x: 0, z: 0 };
        const d = separacion * (k + 1);
        r.push(
          visual(
            l,
            tr,
            base.x + colaDir.x * d,
            base.z + colaDir.z * d,
            Math.atan2(-colaDir.x, -colaDir.z),
            t,
          ),
        );
      });
      return r;
    },
    enLaCalle(t: number): number {
      if (capacidadCola === undefined) return 0;
      let cola = 0;
      for (const l of lista) {
        if (t < l.llegada || t >= l.salida) continue;
        if (buscarTramo(l.tramos, t)?.estado === 'cola-ingreso') cola++;
      }
      return Math.max(0, cola - capacidadCola);
    },
    personasEn(t: number): PersonaVisual[] {
      const r: PersonaVisual[] = [];
      // Los tramos están en orden de inicio (el registro es cronológico).
      for (const p of aPie) {
        if (p.desde > t) break;
        if (t >= p.hasta) continue;
        if (!p.camina) {
          const fin = p.ruta[p.ruta.length - 1]!;
          r.push({ id: p.id, rol: p.rol, x: fin.x, z: fin.z, rumbo: 0, camina: false });
          continue;
        }
        const f = Math.min(1, Math.max(0, (t - p.desde) / (p.hasta - p.desde)));
        const q =
          p.ruta.length > 1
            ? puntoEnPolilinea(p.ruta, f * p.largo)
            : { punto: p.ruta[0]!, rumbo: 0 };
        r.push({ id: p.id, rol: p.rol, x: q.punto.x, z: q.punto.z, rumbo: q.rumbo, camina: true });
      }
      return r;
    },
    eventosEntre(a: number, b: number): Evento[] {
      return ordenEventos.filter((e) => e.t >= a && e.t < b);
    },
    siguienteEvento(t: number): number {
      let lo = 0;
      let hi = ordenInstantes.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (ordenInstantes[mid]! <= t) lo = mid + 1;
        else hi = mid;
      }
      return lo < ordenInstantes.length ? Math.min(ordenInstantes[lo]!, o.fin) : o.fin;
    },
  };
}

/**
 * Punto de una ruta en el metro `m` del recorrido por el eje (los puntos traen su `s`), con el rumbo
 * del tramo dibujado.
 */
function puntoPorMetro(ruta: readonly PuntoDeRuta[], m: number): { punto: Punto; rumbo: number } {
  if (ruta.length === 1) return { punto: ruta[0]!, rumbo: 0 };
  let i = 1;
  while (i < ruta.length - 1 && ruta[i]!.s! < m) i++;
  const a = ruta[i - 1]!;
  const b = ruta[i]!;
  const tramo = b.s! - a.s!;
  const f = tramo > 0 ? Math.min(1, Math.max(0, (m - a.s!) / tramo)) : 1;
  return {
    punto: { x: a.x + (b.x - a.x) * f, z: a.z + (b.z - a.z) * f },
    rumbo: Math.atan2(b.x - a.x, b.z - a.z),
  };
}

function visual(
  l: LineaUnidad,
  tr: Tramo,
  x: number,
  z: number,
  rumbo: number,
  t: number,
): UnidadVisual {
  const listo = l.listo !== null && t >= l.listo;
  let horaPrometida: number | null = null;
  for (const p of l.promesas) if (p.desde <= t) horaPrometida = p.hora;
  return {
    id: l.id,
    placa: l.placa,
    tipoUnidad: l.tipoUnidad,
    x,
    z,
    rumbo,
    estado: tr.estado,
    etapaId: tr.etapaId,
    estacionId: tr.estacionId,
    listo,
    atrasada: !listo && horaPrometida !== null && t > horaPrometida,
    horaPrometida,
    ...(tr.aPie ? { aPie: true as const } : {}),
  };
}
